import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, Save, Trash2, Wand2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import type {
  CreatePromoCodeRequest,
  PromoCode,
  PromoDiscountType,
  PromoScope,
  UpdatePromoCodeRequest,
} from '@/types'
import { PromoScopePicker } from './PromoScopePicker'

interface PromoCodeDrawerProps {
  mode: 'create' | 'edit'
  initial?: PromoCode | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

// Backend stamps every auto-minted receipt thank-you code with this
// description prefix (marketing_promo.go:66). Mirror it client-side so
// the drawer can lock fields + show the right "this is system-owned"
// banner without round-tripping to the server.
const THANK_YOU_DESCRIPTION_PREFIX = 'Receipt thank-you·'

function isAutoThankYouPromo(p: PromoCode | null | undefined): boolean {
  return !!p?.description && p.description.startsWith(THANK_YOU_DESCRIPTION_PREFIX)
}

interface DraftState {
  code: string
  description: string
  discountType: PromoDiscountType
  discountValue: string
  minSubtotal: string
  validFrom: string
  validTo: string
  maxRedemptions: string
  maxPerCustomer: string
  isActive: boolean
  scope: PromoScope
  categoryIds: string[]
  productIds: string[]
}

const EMPTY_DRAFT: DraftState = {
  code: '',
  description: '',
  discountType: 'percent',
  discountValue: '',
  minSubtotal: '',
  validFrom: '',
  validTo: '',
  maxRedemptions: '',
  maxPerCustomer: '1',
  isActive: true,
  scope: 'bill',
  categoryIds: [],
  productIds: [],
}

function promoToDraft(p: PromoCode): DraftState {
  return {
    code: p.code,
    description: p.description ?? '',
    discountType: p.discount_type,
    discountValue: String(p.discount_value),
    minSubtotal: p.min_subtotal != null ? String(p.min_subtotal) : '',
    validFrom: p.valid_from ?? '',
    validTo: p.valid_to ?? '',
    maxRedemptions: p.max_redemptions != null ? String(p.max_redemptions) : '',
    maxPerCustomer: String(p.max_per_customer),
    isActive: p.is_active,
    scope: p.scope ?? 'bill',
    categoryIds: p.category_ids ?? [],
    productIds: p.product_ids ?? [],
  }
}

/**
 * Create/edit drawer for a single promo code. Persists on Save; never
 * partial-saves a field mid-edit. In edit mode, the code field is
 * disabled once a redemption exists (server enforces this too).
 *
 * Keep this thin: the drawer is the form, not the catalog. Catalog
 * lives in AdminPromoCodes.tsx.
 */
export function PromoCodeDrawer({ mode, initial, open, onOpenChange }: PromoCodeDrawerProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)

  // Reset/hydrate whenever a new editing target is picked.
  useEffect(() => {
    if (!open) return
    setDraft(initial ? promoToDraft(initial) : EMPTY_DRAFT)
  }, [open, initial])

  const codeImmutable = mode === 'edit' && (initial?.redemption_count ?? 0) > 0
  // Auto-minted receipt codes are owned by the system. The drawer
  // becomes read-only for them (only Archive remains, so a leaked code
  // can be invalidated). Discount / validity changes for *future* auto
  // codes happen in Settings → Receipt, not here.
  const isAutoCode = mode === 'edit' && isAutoThankYouPromo(initial)
  const fieldsLocked = isAutoCode

  const createMutation = useMutation({
    mutationFn: (data: CreatePromoCodeRequest) => apiClient.createPromoCode(data),
    onSuccess: () => {
      toast({ title: 'Promo created', description: 'Cashier can now apply it at checkout.' })
      queryClient.invalidateQueries({ queryKey: ['promo-codes'] })
      onOpenChange(false)
    },
    onError: (err) => surfaceError(err, toast, 'Create failed'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePromoCodeRequest }) =>
      apiClient.updatePromoCode(id, data),
    onSuccess: () => {
      toast({ title: 'Promo updated' })
      queryClient.invalidateQueries({ queryKey: ['promo-codes'] })
      onOpenChange(false)
    },
    onError: (err) => surfaceError(err, toast, 'Update failed'),
  })

  const archiveMutation = useMutation({
    mutationFn: (id: string) => apiClient.archivePromoCode(id),
    onSuccess: () => {
      toast({ title: 'Promo archived' })
      queryClient.invalidateQueries({ queryKey: ['promo-codes'] })
      onOpenChange(false)
    },
    onError: (err) => surfaceError(err, toast, 'Archive failed'),
  })

  const submitting = createMutation.isPending || updateMutation.isPending || archiveMutation.isPending

  function handleSave() {
    if (!draft.code.trim()) {
      toast({ title: 'Code required', description: 'Enter a code customers will type at checkout.', variant: 'destructive' })
      return
    }
    const numValue = Number(draft.discountValue)
    if (!draft.discountValue.trim() || !Number.isFinite(numValue) || numValue <= 0) {
      toast({ title: 'Invalid value', description: 'Discount value must be greater than 0.', variant: 'destructive' })
      return
    }
    if (draft.discountType === 'percent' && numValue > 100) {
      toast({ title: 'Percent too high', description: 'Percent discount caps at 100.', variant: 'destructive' })
      return
    }

    const minSubtotalNum = draft.minSubtotal.trim() === '' ? undefined : Number(draft.minSubtotal)
    if (minSubtotalNum !== undefined && !Number.isFinite(minSubtotalNum)) {
      toast({ title: 'Invalid minimum', description: 'min_subtotal must be a number.', variant: 'destructive' })
      return
    }
    if (draft.scope === 'items' && draft.categoryIds.length + draft.productIds.length === 0) {
      toast({ title: 'Pick what it applies to', description: 'Choose at least one category or item, or switch to Whole bill.', variant: 'destructive' })
      return
    }
    const scopeFields = {
      scope: draft.scope,
      category_ids: draft.scope === 'items' ? draft.categoryIds : [],
      product_ids: draft.scope === 'items' ? draft.productIds : [],
    }
    const maxRedNum = draft.maxRedemptions.trim() === '' ? undefined : Number(draft.maxRedemptions)
    const maxPCNum = draft.maxPerCustomer.trim() === '' ? 1 : Number(draft.maxPerCustomer)

    if (mode === 'create') {
      const payload: CreatePromoCodeRequest = {
        code: draft.code.toUpperCase(),
        description: draft.description.trim() || undefined,
        discount_type: draft.discountType,
        discount_value: numValue,
        min_subtotal: minSubtotalNum,
        valid_from: draft.validFrom.trim() || undefined,
        valid_to: draft.validTo.trim() || undefined,
        max_redemptions: maxRedNum,
        max_per_customer: maxPCNum,
        ...scopeFields,
      }
      createMutation.mutate(payload)
      return
    }
    if (!initial) return
    const data: UpdatePromoCodeRequest = {
      description: draft.description.trim(),
      discount_type: draft.discountType,
      discount_value: numValue,
      max_per_customer: maxPCNum,
      is_active: draft.isActive,
      ...scopeFields,
    }
    if (!codeImmutable && draft.code.toUpperCase() !== initial.code) {
      data.code = draft.code.toUpperCase()
    }
    if (minSubtotalNum === undefined) {
      data.clear_min_subtotal = true
    } else {
      data.min_subtotal = minSubtotalNum
    }
    if (draft.validFrom.trim() === '') {
      data.clear_valid_from = true
    } else {
      data.valid_from = draft.validFrom.trim()
    }
    if (draft.validTo.trim() === '') {
      data.clear_valid_to = true
    } else {
      data.valid_to = draft.validTo.trim()
    }
    if (maxRedNum === undefined) {
      data.clear_max_redemptions = true
    } else {
      data.max_redemptions = maxRedNum
    }
    updateMutation.mutate({ id: initial.id, data })
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'New promo code' : `Edit ${initial?.code ?? ''}`}</DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Create a code your cashier can type at checkout. Caps and validity window are enforced server-side.'
              : 'Update the discount, caps, or status. The code itself can\'t change once it has been redeemed.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {isAutoCode && (
            <div className="flex items-start gap-3 rounded-md border border-amber-300/70 bg-amber-50 px-3 py-2.5 text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-100">
              <Wand2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <div className="text-sm leading-snug">
                <div className="font-semibold">Comeback coupon</div>
                <p className="mt-0.5">
                  This code was minted automatically when a receipt was printed. Its fields
                  are locked — to change the discount, validity, or copy for future codes,
                  edit your{' '}
                  <span className="font-medium">Comeback coupon</span> settings at the top of
                  the Promo Codes page. You can still <span className="font-medium">Archive</span>{' '}
                  this one code below (e.g. if the receipt was lost or the code was leaked).
                </p>
              </div>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="promo-code">Code <span className="text-destructive">*</span></Label>
              <Input
                id="promo-code"
                value={draft.code}
                onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })}
                placeholder="BIRTHDAY25"
                maxLength={40}
                disabled={codeImmutable || fieldsLocked}
                autoComplete="off"
                className="font-mono"
              />
              {codeImmutable && !fieldsLocked && (
                <p className="text-sm text-muted-foreground">
                  Locked — code has already been redeemed.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="promo-desc">Description</Label>
              <Input
                id="promo-desc"
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                placeholder="Birthday — 25% off"
                maxLength={200}
                disabled={fieldsLocked}
              />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-[1fr_1fr_1fr]">
            <div className="space-y-1.5">
              <Label>Discount</Label>
              <Select
                value={draft.discountType}
                onValueChange={(v) => setDraft({ ...draft, discountType: v as PromoDiscountType })}
                disabled={fieldsLocked}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Percent (%)</SelectItem>
                  <SelectItem value="flat">Flat (PKR)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="promo-value">Value <span className="text-destructive">*</span></Label>
              <Input
                id="promo-value"
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                value={draft.discountValue}
                onChange={(e) => setDraft({ ...draft, discountValue: e.target.value })}
                placeholder={draft.discountType === 'percent' ? 'e.g. 25' : 'e.g. 500'}
                disabled={fieldsLocked}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="promo-min">{draft.scope === 'items' ? 'Min spend on these (PKR)' : 'Min subtotal (PKR)'}</Label>
              <Input
                id="promo-min"
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                value={draft.minSubtotal}
                onChange={(e) => setDraft({ ...draft, minSubtotal: e.target.value })}
                placeholder="Optional"
                disabled={fieldsLocked}
              />
            </div>
          </div>

          {!isAutoCode && (
            <PromoScopePicker
              scope={draft.scope}
              categoryIds={draft.categoryIds}
              productIds={draft.productIds}
              disabled={fieldsLocked}
              onChange={(next) => setDraft((d) => ({ ...d, ...next }))}
            />
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="promo-from">Valid from</Label>
              <Input
                id="promo-from"
                type="date"
                value={draft.validFrom}
                onChange={(e) => setDraft({ ...draft, validFrom: e.target.value })}
                disabled={fieldsLocked}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="promo-to">Valid to</Label>
              <Input
                id="promo-to"
                type="date"
                value={draft.validTo}
                onChange={(e) => setDraft({ ...draft, validTo: e.target.value })}
                disabled={fieldsLocked}
              />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="promo-max">Max redemptions (total)</Label>
              <Input
                id="promo-max"
                type="number"
                inputMode="numeric"
                min={1}
                step="1"
                value={draft.maxRedemptions}
                onChange={(e) => setDraft({ ...draft, maxRedemptions: e.target.value })}
                placeholder="Unlimited"
                disabled={fieldsLocked}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="promo-per">Max per customer</Label>
              <Input
                id="promo-per"
                type="number"
                inputMode="numeric"
                min={1}
                step="1"
                value={draft.maxPerCustomer}
                onChange={(e) => setDraft({ ...draft, maxPerCustomer: e.target.value })}
                disabled={fieldsLocked}
              />
            </div>
          </div>

          {mode === 'edit' && (
            <div className="flex items-center justify-between rounded-md border border-border/70 bg-muted/30 px-3 py-2">
              <div className="space-y-0.5">
                <Label htmlFor="promo-active" className="text-sm">Active</Label>
                <p className="text-sm text-muted-foreground">
                  Inactive codes can't be redeemed at the counter.
                </p>
              </div>
              <Switch
                id="promo-active"
                checked={draft.isActive}
                onCheckedChange={(checked) => setDraft({ ...draft, isActive: checked })}
                disabled={fieldsLocked}
              />
            </div>
          )}

          {mode === 'edit' && initial && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 rounded-md border border-border/70 bg-background/60 px-3 py-2 text-sm">
              <div>
                <div className="text-muted-foreground uppercase tracking-wider text-sm">Redemptions</div>
                <div className="mt-0.5 font-semibold tabular-nums">{initial.redemption_count}</div>
              </div>
              <div>
                <div className="text-muted-foreground uppercase tracking-wider text-sm">Revenue (PKR)</div>
                <div className="mt-0.5 font-semibold tabular-nums">
                  {initial.revenue_attributed_pkr.toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground uppercase tracking-wider text-sm">Created</div>
                <div className="mt-0.5 font-semibold tabular-nums">
                  {new Date(initial.created_at).toLocaleDateString()}
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:justify-between">
          <div>
            {mode === 'edit' && initial && initial.is_active && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => archiveMutation.mutate(initial.id)}
                disabled={submitting}
                className="text-destructive hover:text-destructive gap-1.5"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Archive
              </Button>
            )}
          </div>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              {fieldsLocked ? 'Close' : 'Cancel'}
            </Button>
            {!fieldsLocked && (
              <Button type="button" onClick={handleSave} disabled={submitting} className="gap-1.5">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {mode === 'create' ? 'Create' : 'Save'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function surfaceError(err: unknown, toast: ReturnType<typeof useToast>['toast'], title: string) {
  const msg = err instanceof Error ? err.message : String(err)
  toast({ title, description: msg, variant: 'destructive' })
}
